import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Trans, useTranslation } from 'react-i18next';

import { dashboardStatisticsQueryOptions, useDashboardStatistics } from '~/api/statistics';
import { ErrorState, ExternalLink, StatBlock } from '~/components';
import { srOnlyStyle } from '~/styles/utilities.css';
import { clsx } from '~/styles/utils';

import { statsGridAverageStyle, statsGridStyle, statsSectionStyle } from './StatsSummary.css';
import { StatsSummarySkeleton } from './StatsSummary.skeleton';

export const StatsSummary = () => {
  const { data: statistics, isError, isPending } = useDashboardStatistics();
  const queryClient = useQueryClient();

  const { t } = useTranslation(['dashboard']);

  if (isPending) return <StatsSummarySkeleton />;

  if (isError)
    return (
      <section className={statsSectionStyle}>
        <ErrorState onRetry={() => queryClient.resetQueries(dashboardStatisticsQueryOptions)} title={t('dashboard:stats.error.title')} />
      </section>
    );

  const showAverageReceivedPastYears = statistics.averageCopyrightPastYears > 0;
  const thisYear = dayjs().year();

  return (
    <section className={statsSectionStyle}>
      <h2 className={srOnlyStyle}>{t('dashboard:stats.title')}</h2>

      <dl className={clsx(statsGridStyle, showAverageReceivedPastYears && statsGridAverageStyle)}>
        <StatBlock
          illustration="Coins"
          illustrationStrokeWidth={1}
          label={t('dashboard:stats.invoicedSumThisYear.label', { year: thisYear })}
          tooltip={<Trans components={{ a: <ExternalLink /> }} i18nKey="dashboard:stats.invoicedSumThisYear.tooltip" />}
          value={statistics.totalIncomeThisYear}
        />

        <StatBlock
          illustration="Copyright"
          illustrationStrokeWidth={1.4}
          label={t('dashboard:stats.copyrightThisYear.label', { year: thisYear })}
          tooltip={<Trans components={{ a: <ExternalLink /> }} i18nKey="dashboard:stats.copyrightThisYear.tooltip" />}
          value={statistics.totalCopyrightThisYear}
        />

        {showAverageReceivedPastYears && (
          <StatBlock
            illustration="Calculator"
            illustrationStrokeWidth={1.7}
            label={t('dashboard:stats.copyrightAverage.label')}
            tooltip={<Trans components={{ a: <ExternalLink /> }} i18nKey="dashboard:stats.copyrightAverage.tooltip" />}
            value={statistics.averageCopyrightPastYears}
          />
        )}
      </dl>
    </section>
  );
};
